// Chips (DESIGN §5.4): filtro (pill, ativo em tinta) e status de contato (painel).
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { StatusContato } from "@grindset/server/contratos";
import { cn } from "../lib/cn";
import { Icones, type TipoIcone } from "./icones";

export interface PropsChip extends Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "id" | "tabIndex" | "onKeyDown" | "onFocus" | "aria-label"
> {
  ativo?: boolean;
  aoClicar?: () => void;
  children: ReactNode;
  /** Contagem depois do texto ("Manhã 3"), em negrito largo (mockup Equipe, 28/09). */
  contagem?: number;
  desabilitado?: boolean;
  /** role dentro de um grupo: "radio" (escolha única) ou "tab". */
  papel?: "radio" | "tab" | "checkbox";
  className?: string;
}

/**
 * Chip de filtro: h-9, px 14, alvo de toque 48 com área expandida. `ref`, `tabIndex` e `onKeyDown` servem aos
 * grupos com foco itinerante (setas), como o `DiasDaSemana`.
 */
export const Chip = forwardRef<HTMLButtonElement, PropsChip>(function Chip(
  { ativo, aoClicar, children, contagem, desabilitado, papel = "radio", className, ...resto },
  ref,
) {
  const estado = papel === "tab" ? { "aria-selected": !!ativo } : { "aria-checked": !!ativo };
  return (
    <button
      ref={ref}
      type="button"
      {...resto}
      role={papel}
      {...estado}
      onClick={aoClicar}
      disabled={desabilitado}
      className={cn(
        "relative inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-rotulo whitespace-nowrap",
        "after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-[''] disabled:opacity-40",
        ativo
          ? "border-tinta bg-tinta text-sobre-tinta"
          : "border-fio-controle bg-painel text-texto-2 hover:text-tinta",
        className,
      )}
    >
      {children}
      {contagem !== undefined && (
        <b className={cn("placar font-bold", ativo ? "text-sobre-tinta" : "text-tinta")}>{contagem}</b>
      )}
    </button>
  );
});

/** Linha de chips que rola na horizontal, sangrando até a margem. */
export function LinhaChips({
  rotulo,
  children,
  papel = "radiogroup",
  className,
}: {
  rotulo: string;
  children: ReactNode;
  papel?: "radiogroup" | "tablist" | "group";
  className?: string;
}) {
  return (
    <div
      role={papel}
      aria-label={rotulo}
      className={cn("no-scrollbar -mx-margem flex gap-2 overflow-x-auto px-margem py-1.5", className)}
    >
      {children}
    </div>
  );
}

const CONTATO: Record<StatusContato, { classe: string; icone?: TipoIcone; ponto?: boolean }> = {
  SEM_CONTATO: { classe: "bg-painel border border-fio-controle text-texto-2", ponto: true },
  AUTOMATICO_ENVIADO: { classe: "bg-recuo text-texto-2", icone: Icones.automacoes },
  EQUIPE_ENVIOU: { classe: "bg-recuo text-tinta", icone: Icones.enviado },
  RESPONDEU: { classe: "bg-info-fundo text-info", icone: Icones.respondeu },
  VOLTOU: { classe: "bg-ok-fundo text-ok", icone: Icones.confirmar },
};

const TEXTO_CONTATO: Record<StatusContato, string> = {
  SEM_CONTATO: "Sem contato",
  AUTOMATICO_ENVIADO: "Automático enviado",
  EQUIPE_ENVIOU: "Equipe enviou",
  RESPONDEU: "Respondeu",
  VOLTOU: "Voltou",
};

/**
 * Status de contato da tabela de risco (SPEC §4.10). Para EQUIPE_ENVIOU, passe `autor`:
 * "Você" (se foi o usuário logado) ou o primeiro nome de quem enviou.
 */
export function ChipContato({
  status,
  autor,
  className,
}: {
  /** null: quem vê não tem `alunos.ver` (a API não manda o status); não mostra nada. */
  status: StatusContato | null;
  autor?: string;
  className?: string;
}) {
  if (!status) return null;
  const c = CONTATO[status];
  const Icone = c.icone;
  const texto = status === "EQUIPE_ENVIOU" && autor ? `${autor} enviou` : TEXTO_CONTATO[status];
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-celula px-2 text-rotulo whitespace-nowrap",
        c.classe,
        className,
      )}
    >
      {c.ponto && <span aria-hidden className="size-1.5 rounded-full bg-texto-3" />}
      {Icone && <Icone size={14} aria-hidden />}
      {texto}
    </span>
  );
}
